/**
 * universeText: display-only text helpers for the Brand Universe.
 *
 * Nothing here changes a stored value. Fact names keep whatever prefix the agent
 * wrote; these helpers only decide how the name reads on screen.
 */

/** Prefixes the agents have written onto fact names over time. Display only. */
const AGENT_PREFIXES = [
  "Competitive Pattern",
  "Competitor",
  "Active Campaign",
  "Market Trend",
  "Channels",
  "Personality",
  "Visual Identity",
  "Audience",
  "Trend",
  "Strategy",
  "Avoid",
];

const escapeRe = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const AGENT_PREFIX_RE = new RegExp(`^\\s*(?:${AGENT_PREFIXES.map(escapeRe).join("|")}):\\s*`, "i");

/**
 * The fact name as it should read: the group label prefix the list has always
 * stripped (e.g. "Voice & tone: ..."), then one known agent prefix. Returns the
 * original when stripping would leave nothing.
 */
export function stripFactPrefix(label: string, groupLabel?: string): string {
  let out = label;
  const gl = groupLabel?.trim();
  if (gl) out = out.replace(new RegExp(`^\\s*${escapeRe(gl)}:\\s*`, "i"), "");
  out = out.replace(AGENT_PREFIX_RE, "");
  return out.trim() ? out : label;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "3 Sep". Written out rather than Intl, which prints "Sept" in en-GB. */
export function formatDayMonth(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "13 Jul 2026". */
export function formatDayMonthYear(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

const WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

/** "five" for 5; digits past ten. */
export function numberWord(n: number): string {
  return WORDS[n] ?? String(n);
}

/** "A", "A and B", "A, B and C". */
export function joinAnd(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

const STOP_WORDS = new Set(["brand", "the", "and", "of", "our"]);

function nameTokens(label: string): Set<string> {
  const words = stripFactPrefix(label)
    .toLocaleLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w && !STOP_WORDS.has(w));
  return new Set(words);
}

/**
 * Two facts whose names say nearly the same thing (token Jaccard at least 0.5
 * after dropping agent prefixes and a few stop words) while their descriptions
 * differ. That pair is worth a reconcile ask; the helper decides nothing else.
 */
export function isNearIdenticalName(
  a: { label: string; desc: string },
  b: { label: string; desc: string },
): boolean {
  if (a.desc.trim() === b.desc.trim()) return false;
  const ta = nameTokens(a.label);
  const tb = nameTokens(b.label);
  if (!ta.size || !tb.size) return false;
  let shared = 0;
  ta.forEach((t) => {
    if (tb.has(t)) shared += 1;
  });
  const union = ta.size + tb.size - shared;
  return union > 0 && shared / union >= 0.5;
}
